import React, { useCallback, useRef } from 'react';
import { RowDataBoundEventArgs, RowSelectingEventArgs } from '@syncfusion/ej2-react-grids';
import { TreeGridComponent, ColumnsDirective as TreeColumnsDirective, ColumnDirective as TreeColumnDirective } from '@syncfusion/ej2-react-treegrid';
import * as Inovx from '@shared';
import { IConfigurationTreeNode } from './types';
import { TooltipComponent } from '@syncfusion/ej2-react-popups';

var UrlUtilities = Inovx.Shared.Common.UrlUtilities;

declare var InovxLoader: any;

export interface AssetSelectionSectionProps {
    treeGridRef: React.RefObject<TreeGridComponent | null>;
    treeDataSource: IConfigurationTreeNode[];
    treeGridRenderKey: number;
    onRowSelecting: (event: RowSelectingEventArgs) => void;
    onRowSelected: (event: any) => void;
    onRowDataBound: (args: RowDataBoundEventArgs) => void;
    onRowExpanded: (event: any) => void;
    onRowCollapsed: (event: any) => void;
    onOpenContextMenu: (clientX: number, clientY: number, node: IConfigurationTreeNode, ctrlKey?: boolean) => void;
}

const AssetSelectionSection: React.FC<AssetSelectionSectionProps> = ({
    treeGridRef,
    treeDataSource,
    treeGridRenderKey,
    onRowSelecting,
    onRowSelected,
    onRowDataBound,
    onRowExpanded,
    onRowCollapsed,
    onOpenContextMenu
}) => {
    const treeContainerRef = useRef<HTMLDivElement | null>(null);
    const toolTipInstance = useRef<TooltipComponent>(null);
    const selectionScrollTopRef = useRef<number | null>(null);
    const remountScrollTopRef = useRef<number | null>(null);

    const getTreeScrollElement = useCallback(() => {
        return treeContainerRef.current?.querySelector<HTMLElement>('.e-content') || null;
    }, []);

    const captureTreeScrollPosition = useCallback(() => {
        selectionScrollTopRef.current = getTreeScrollElement()?.scrollTop ?? null;
    }, [getTreeScrollElement]);

    const restoreTreeScrollPosition = useCallback(() => {
        const scrollTop = selectionScrollTopRef.current;
        if (scrollTop === null) {
            return;
        }

        [0, 40, 120].forEach((delay) => {
            window.setTimeout(() => {
                const scrollElement = getTreeScrollElement();
                if (scrollElement) {
                    scrollElement.scrollTop = scrollTop;
                }
            }, delay);
        });
    }, [getTreeScrollElement]);

    const handleRowSelecting = useCallback((event: RowSelectingEventArgs) => {
        captureTreeScrollPosition();
        onRowSelecting(event);
    }, [captureTreeScrollPosition, onRowSelecting]);

    const handleRowSelected = useCallback((event: any) => {
        onRowSelected(event);
        restoreTreeScrollPosition();
    }, [onRowSelected, restoreTreeScrollPosition]);

    React.useEffect(() => {
        return () => {
            remountScrollTopRef.current = getTreeScrollElement()?.scrollTop ?? null;
        };
    }, [getTreeScrollElement, treeGridRenderKey]);

    React.useEffect(() => {
        const scrollTop = remountScrollTopRef.current;
        if (scrollTop === null) {
            return;
        }

        [0, 40, 120].forEach((delay) => {
            window.setTimeout(() => {
                const scrollElement = getTreeScrollElement();
                if (scrollElement) {
                    scrollElement.scrollTop = scrollTop;
                }
            }, delay);
        });
    }, [getTreeScrollElement, treeGridRenderKey]);

    const renderTreeNameTemplate = useCallback((node: IConfigurationTreeNode) => {
        const iconUrl = `${UrlUtilities.getBaseApplicationUrl()}/images/schema/${node.IconFileName}`;

        return (
            <div className="facility-hierarchy-treegrid-name configuration-form-tree-name">
                <div className="facility-hierarchy-treegrid-name-img configuration-form-tree-name-img">
                    <img className="e-treeoverviewimage" src={iconUrl} alt={node.NodeType} style={{ width: 20, height: 20, verticalAlign: 'middle' }} />
                </div>
                <div className={`facility-hierarchy-treegrid-name-label configuration-form-tree-name-label${node.NodeType === 'schema' ? ' font-bold' : ''}`} title={node.Name}>
                    {node.Name}
                </div>
            </div>
        );
    }, []);

    const renderTreeActionTemplate = useCallback((node: IConfigurationTreeNode) => {
        return (
            <button
                type="button"
                className="configuration-form-tree-action-button"
                title={InovxLoader.t('facility:grid.commands')}
                onClick={(event) => {
                    event.stopPropagation();
                    const rect = event.currentTarget.getBoundingClientRect();
                    onOpenContextMenu(rect.left + rect.width - 8, rect.bottom + 4, node, event.ctrlKey);
                }}
            >
                <i className="fa fa-ellipsis-v" />
            </button>
        );
    }, [onOpenContextMenu]);

    return (
        <div ref={treeContainerRef} className="configuration-form-left-panel">
            <TooltipComponent
                ref={toolTipInstance}
                target=".facility-hierarchy-treegrid-name-label"
                beforeRender={args => {
                    const el = args.target as HTMLElement;
                    if (!el) {
                        args.cancel = true;
                        return;
                    }

                    // Show tooltip only if text is truncated
                    if (el.scrollWidth <= el.clientWidth) {
                        args.cancel = true;
                    }
                }}
                showTipPointer={false}
                cssClass="knowledge-view-name-tooltip"
                style={{ height: '100%' }}
            >
                <TreeGridComponent
                    key={treeGridRenderKey}
                    ref={treeGridRef}
                    className="configuration-form-treegrid"
                    dataSource={treeDataSource}
                    childMapping="Children"
                    expandStateMapping="IsExpanded"
                    treeColumnIndex={0}
                    height="100%"
                    rowHeight={36}
                    selectionSettings={{ cellSelectionMode: 'Flow', mode: 'Row', type: 'Single', enableToggle: false }}
                    expanded={onRowExpanded}
                    collapsed={onRowCollapsed}
                    rowSelecting={handleRowSelecting}
                    rowSelected={handleRowSelected}
                    rowDataBound={onRowDataBound}
                    locale={InovxLoader.getSyncfusionJS2Locale() || undefined}
                >
                    <TreeColumnsDirective>
                        <TreeColumnDirective field="Name" headerText={InovxLoader.t('facility:grid.name')} width="220" template={renderTreeNameTemplate} />
                        <TreeColumnDirective field="TreeNodeKey" headerText="TreeNodeKey" visible={false} isPrimaryKey={true} />
                        <TreeColumnDirective field="Actions" headerText="" width="40" template={renderTreeActionTemplate} />
                    </TreeColumnsDirective>
                </TreeGridComponent>
            </TooltipComponent>
        </div>
    );
};

export default AssetSelectionSection;
